import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ReportData {
  dateRange: { from: Date; to: Date };
  stats: {
    totalMatched: number;
    totalUnmatched: number;
    blockedInvoices: number;
    riskySavings: number;
  };
  monthlyData: Array<{
    month: string;
    matched: number;
    unmatched: number;
    savings: number;
  }>;
  vendorData: Array<{
    vendor: string;
    amount: number;
    count: number;
  }>;
  runwayData: Array<{
    month: string;
    cashFlow: number;
    cumulative: number;
  }>;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', { 
    style: 'currency', 
    currency: 'INR', 
    maximumFractionDigits: 0 
  }).format(amount);
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

export function generateReconciliationReportPDF(data: ReportData): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(6, 182, 212); // Primary cyan
  doc.text('Sentinel', 14, yPos);
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text('Financial Reconciliation Report', 14, yPos + 8);
  
  // Date range
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  const dateRangeText = `${formatDate(data.dateRange.from)} - ${formatDate(data.dateRange.to)}`;
  doc.text(dateRangeText, pageWidth - 14 - doc.getTextWidth(dateRangeText), yPos);
  
  // Generated timestamp
  const generatedText = `Generated: ${new Date().toLocaleString('en-IN')}`;
  doc.text(generatedText, pageWidth - 14 - doc.getTextWidth(generatedText), yPos + 6);

  yPos += 25;

  // Divider
  doc.setDrawColor(230, 230, 230);
  doc.line(14, yPos, pageWidth - 14, yPos);
  yPos += 10;

  // Executive Summary
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  doc.text('Executive Summary', 14, yPos);
  yPos += 10;

  // Stats boxes (simplified as table)
  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: [
      ['Matched Transactions', data.stats.totalMatched.toString()],
      ['Unmatched Transactions', data.stats.totalUnmatched.toString()],
      ['Blocked Invoices', data.stats.blockedInvoices.toString()],
      ['Savings from Blocked Invoices', formatCurrency(data.stats.riskySavings)],
    ],
    theme: 'striped',
    headStyles: { 
      fillColor: [6, 182, 212],
      textColor: 255,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 10,
      cellPadding: 4,
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 80 },
      1: { halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Monthly Reconciliation Trends
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  doc.text('Monthly Reconciliation Trends', 14, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Month', 'Matched', 'Unmatched', 'Savings']],
    body: data.monthlyData.map(m => [
      m.month,
      m.matched.toString(),
      m.unmatched.toString(),
      formatCurrency(m.savings),
    ]),
    theme: 'striped',
    headStyles: { 
      fillColor: [6, 182, 212],
      textColor: 255,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 9,
      cellPadding: 3,
    },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Check if we need a new page
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  // Vendor Spending Analysis
  if (data.vendorData.length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text('Vendor Spending Analysis', 14, yPos);
    yPos += 8;

    // Sort by amount and take top 15
    const topVendors = [...data.vendorData]
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 15);

    autoTable(doc, {
      startY: yPos,
      head: [['Vendor', 'Invoice Count', 'Total Amount']],
      body: topVendors.map(v => [
        v.vendor.length > 30 ? v.vendor.substring(0, 27) + '...' : v.vendor,
        v.count.toString(),
        formatCurrency(v.amount),
      ]),
      theme: 'striped',
      headStyles: { 
        fillColor: [16, 185, 129], // Green
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { halign: 'center', cellWidth: 35 },
        2: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Check if we need a new page
  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  // Cash Flow Summary
  if (data.runwayData.length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text('Cash Flow Summary', 14, yPos);
    yPos += 8;

    autoTable(doc, {
      startY: yPos,
      head: [['Month', 'Monthly Flow', 'Cumulative']],
      body: data.runwayData.map(r => [
        r.month,
        formatCurrency(r.cashFlow),
        formatCurrency(r.cumulative),
      ]),
      theme: 'striped',
      headStyles: { 
        fillColor: [139, 92, 246], // Purple
        textColor: 255,
        fontStyle: 'bold'
      },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
      margin: { left: 14, right: 14 },
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;
  }

  // Footer on all pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    
    // Page number
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    
    // Footer text
    doc.text(
      'Generated by Sentinel - Your AI Finance Guardian',
      14,
      doc.internal.pageSize.getHeight() - 10
    );
  }

  // Generate filename with date
  const fileName = `sentinel-report-${formatDate(data.dateRange.from)}-to-${formatDate(data.dateRange.to)}.pdf`
    .replace(/\s/g, '-')
    .toLowerCase();

  // Download
  doc.save(fileName);
}

export function generateInvoiceListPDF(
  invoices: Array<{
    vendor: string;
    invoice_no: string;
    date: string;
    amount: number;
    status: string;
    risk_score: number | null;
  }>,
  title: string = 'Invoice Report'
): void {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Header
  doc.setFontSize(20);
  doc.setTextColor(6, 182, 212);
  doc.text('Sentinel', 14, yPos);
  
  doc.setFontSize(14);
  doc.setTextColor(30, 30, 30);
  doc.text(title, 14, yPos + 10);
  
  doc.setFontSize(10);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, pageWidth - 70, yPos);
  doc.text(`Total Invoices: ${invoices.length}`, pageWidth - 70, yPos + 6);

  yPos += 25;

  // Invoice table
  autoTable(doc, {
    startY: yPos,
    head: [['Vendor', 'Invoice #', 'Date', 'Amount', 'Status', 'Risk']],
    body: invoices.map(inv => [
      inv.vendor.length > 20 ? inv.vendor.substring(0, 17) + '...' : inv.vendor,
      inv.invoice_no,
      new Date(inv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }),
      formatCurrency(inv.amount),
      inv.status.charAt(0).toUpperCase() + inv.status.slice(1),
      inv.risk_score ? `${inv.risk_score}%` : '-',
    ]),
    theme: 'striped',
    headStyles: { 
      fillColor: [6, 182, 212],
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9,
    },
    styles: {
      fontSize: 8,
      cellPadding: 2,
    },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 30 },
      2: { cellWidth: 25 },
      3: { halign: 'right', cellWidth: 30 },
      4: { halign: 'center', cellWidth: 25 },
      5: { halign: 'center', cellWidth: 20 },
    },
    margin: { left: 14, right: 14 },
    didDrawCell: (data) => {
      // Color-code status column
      if (data.column.index === 4 && data.cell.section === 'body') {
        const status = data.cell.raw as string;
        if (status === 'Blocked') {
          doc.setTextColor(239, 68, 68); // Red
        } else if (status === 'Approved') {
          doc.setTextColor(16, 185, 129); // Green
        }
      }
      // Color-code high risk
      if (data.column.index === 5 && data.cell.section === 'body') {
        const risk = data.cell.raw as string;
        const riskNum = parseInt(risk);
        if (riskNum >= 70) {
          doc.setTextColor(239, 68, 68); // Red
        } else if (riskNum >= 50) {
          doc.setTextColor(245, 158, 11); // Orange
        }
      }
    },
  });

  // Summary
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const approved = invoices.filter(i => i.status === 'approved').length;
  const blocked = invoices.filter(i => i.status === 'blocked').length;
  const pending = invoices.filter(i => i.status === 'pending').length;

  yPos = (doc as any).lastAutoTable.finalY + 10;

  doc.setFontSize(10);
  doc.setTextColor(30, 30, 30);
  doc.text(`Summary: ${approved} Approved, ${blocked} Blocked, ${pending} Pending`, 14, yPos);
  doc.text(`Total Value: ${formatCurrency(totalAmount)}`, 14, yPos + 6);

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  doc.save(`sentinel-invoices-${new Date().toISOString().split('T')[0]}.pdf`);
}
